import type { IAddress } from "@bbj-types/address";
import type {
  IOrganisation,
  IOrganisationBeneficiaryConfiguration,
  IOrganisationFUP,
  TAppointmentReminder,
} from "@bbj-types/organisation";
import type { OrientationType } from "@bbj-types/team/enum";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import { axiosSettings } from "./axios";
import type { IGetOrganisationResponse } from "./getOrganisation";

export type TUpdateTeamAddCmd = {
  defaultCer: ObjectId;
  name: string;
  place: string;
  type: OrientationType[];
  address?: IAddress | null;
  email?: string;
  members?: ObjectId[];
  phone?: string;
  showConvocation?: boolean;
};

export type TUpdateTeamUpdateCmd = {
  _id: ObjectId;
  address?: IAddress | null;
  defaultCer?: ObjectId;
  email?: string;
  members?: ObjectId[];
  name?: string;
  phone?: string;
  place?: string;
  showConvocation?: boolean;
  type?: OrientationType[];
};

export type TUpdateOrganisationBody = Partial<
  Pick<IOrganisation, "name" | "places" | "coachingStepRules"> & {
    cer: Partial<Omit<IOrganisation["cer"], "apiKey" | "apiUrl">>;
    teamsCmd: { add?: TUpdateTeamAddCmd[]; update?: TUpdateTeamUpdateCmd[] };
    features: {
      stonly?: { active: boolean };
      google?: { active: boolean };
      action?: { requiredContactDetails: string[] };
      export?: { "candidate.limit": number; "candidate.limitBeforeNotification": number };
      appointmentReminder?: TAppointmentReminder;
    };
    beneficiaryConfiguration: Pick<IOrganisationBeneficiaryConfiguration, "meetingActionTypes">;
    fup?: Pick<IOrganisationFUP, "template">;
    configuration: Pick<IOrganisation["configuration"], "helpCenterUrl">;
  }
>;

export const useUpdateOrganisation = () => {
  const [state, execute, cancel] = axiosSettings<IGetOrganisationResponse, TUpdateOrganisationBody, unknown>(
    {
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: TUpdateOrganisationBody) => {
      return execute({
        url: `/organisations/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
